import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from './api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const initializedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [reqRes, convosRes] = await Promise.all([
        api.get('/friends/requests'),
        api.get('/messages/conversations')
      ]);

      setPendingRequests(reqRes.data.requests.length);

      const convos = convosRes.data.conversations;

      if (!initializedRef.current) {
        // On first load, mark all existing conversations as read so we only
        // surface activity that happens after the user opens the app.
        convos.forEach(c => {
          if (!localStorage.getItem(`last_read_${c.id}`)) {
            localStorage.setItem(`last_read_${c.id}`, Date.now().toString());
          }
        });
        initializedRef.current = true;
      }

      const unread = convos.filter(c => {
        if (!c.last_message) return false;
        const lastRead = localStorage.getItem(`last_read_${c.id}`);
        if (!lastRead) return true;
        return new Date(c.updated_at).getTime() > parseInt(lastRead);
      }).length;

      setUnreadMessages(unread);
    } catch {}
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      setPendingRequests(0);
      setUnreadMessages(0);
      initializedRef.current = false;
      return;
    }
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [user, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const markConversationRead = useCallback((id) => {
    localStorage.setItem(`last_read_${id}`, Date.now().toString());
    setUnreadMessages(prev => Math.max(0, prev - 1));
  }, []);

  return (
    <NotificationContext.Provider value={{ pendingRequests, unreadMessages, markConversationRead, refresh }}>
      {children}
    </NotificationContext.Provider>
  );
}
